import type { DamageTable } from '../types/damage-table';
import { Side } from '../types/side';
import { Motion } from '../types/motion';
import type { Unit } from '../types/unit';
import { MovementType } from '../types/unit';
import type { WeaponData } from '../types/weapon';
import type {
  Veterancy,
  Cohesion,
} from '../types/veterancy-cohesion';
import {
  VETERANCY_MODIFIERS_MAP,
  COHESION_MODIFIERS_MAP,
  getTraitModifiersMap,
} from '../types/veterancy-cohesion';
import { DamageTableProcessor } from './DamageTableProcessor';
import extractDamageFamilyProperties from '../utils/extract-damage-family-properties';
import { isTraitActive, type TraitActivationContext } from './trait-activation';
import { getTraitEffectDescriptions } from './trait-effect-descriptions';

const MAX_SUPPRESSION = 500;
const EXTRA_SUPPRESSION_DAMAGE_PER_DAMAGE = 0;

/*
 * Simulation scope: GSR (_gsr), recon (_reco), and jammer (_jammer) interactions are not modeled
 * beyond generic traits. Amphibious water speed is not part of this calculator (see terrain-speed).
 */

/**
 * Event result types for simulation
 */
export enum EventResultType {
  OPTIMAL = 'optimal',
  AVERAGE = 'average',
  SIMULATION = 'simulation',
}

/**
 * Weapon event types matching WarYes structure
 */
export type WeaponEvent = AimEvent | ReloadEvent | ShotEvent | MissileLaunchEvent | MissileHitEvent;

export interface AimEvent {
  time: number;
  type: 'aim';
  weapon: string;
  weaponName: string;
  duration: number;
}

export interface ReloadEvent {
  time: number;
  type: 'reload';
  weapon: string;
  weaponName: string;
  duration: number;
}

export interface ShotEvent {
  time: number;
  type: 'shot';
  weapon: string;
  weaponName: string;
  damage: number;
  accuracy: number;
  hit: boolean;
  remainingHp?: number;
}

export interface MissileLaunchEvent {
  time: number;
  type: 'missile-launch';
  weapon: string;
  weaponName: string;
  duration: number;
}

export interface MissileHitEvent {
  time: number;
  type: 'missile-hit';
  weapon: string;
  weaponName: string;
  damage: number;
  missileTravelTimeToTarget: number;
  accuracy: number;
  hit: boolean;
  remainingHp?: number;
}

/**
 * Result of damage calculation
 */
export interface DamageCalculationResult {
  damage: number;
  damagePerSecond: number;
  averageDamagePerSecond: number;
  damageMultiplier: number;
  suppressionDamage: number;
  suppressionMultiplier: number;
  flightTimeOfOneMissile?: number;
  timeToKill: number;
  shotsToKill: number;
  shotsToKillWithAccuracy: number;
  shotsToMaxSuppression: number;
  averageTimeToKill: number;
  suppressionTimeToKill: number;
  suppressionTimeToKillWithAccuracy: number;
  shotsToMaxSuppressionWithAccuracy: number;
  accuracy: number;
  canTarget: boolean;
  weapon?: WeaponData;
  weaponIndex?: number;
  activeTraits?: {
    source: Array<{
      traitName: string;
      effects: string[];
      isActive: boolean;
      activationReason?: string;
      activationType: 'auto' | 'manual' | 'unknown';
    }>;
    target: Array<{
      traitName: string;
      effects: string[];
      isActive: boolean;
      activationReason?: string;
      activationType: 'auto' | 'manual' | 'unknown';
    }>;
  };
}

/**
 * Determine if an event hits based on accuracy
 */
function didEventHit(accuracy: number): boolean {
  const randomNumber = Math.random();
  return randomNumber <= accuracy;
}

/**
 * Generate events for gun weapons
 */
function generateGunEvents(
  hp: number,
  weaponProperties: {
    name: string;
    prettyName: string;
    aimTime: number;
    reloadTime: number;
    timeBetweenShots: number;
    salvoLength: number;
    damagePerShot: number;
    accuracy: number;
    simultaneousProjectiles: number;
  }
): WeaponEvent[] {
  const events: WeaponEvent[] = [];
  let time = 0;

  events.push({
    time,
    type: 'aim',
    duration: weaponProperties.aimTime,
    weapon: weaponProperties.name,
    weaponName: weaponProperties.prettyName,
  });

  time += weaponProperties.aimTime;

  while (hp > 0) {
    for (let shotNumber = 0; shotNumber < weaponProperties.salvoLength; shotNumber++) {
      for (let j = 0; j < weaponProperties.simultaneousProjectiles; j++) {
        const didShotHit = didEventHit(weaponProperties.accuracy);

        if (didShotHit) hp -= weaponProperties.damagePerShot;

        events.push({
          time,
          type: 'shot',
          damage: weaponProperties.damagePerShot,
          accuracy: weaponProperties.accuracy,
          weapon: weaponProperties.name,
          weaponName: weaponProperties.prettyName,
          hit: didShotHit,
          remainingHp: hp,
        });

        if (j > 1) {
          shotNumber++;
        }

        if (hp <= 0) {
          break;
        }
      }

      if (hp <= 0) {
        break;
      }

      // only add time between shots if it's about to reload
      if (shotNumber < weaponProperties.salvoLength - 1) {
        time += weaponProperties.timeBetweenShots;
      }
    }

    if (hp <= 0) {
      break;
    }

    events.push({
      time,
      type: 'reload',
      duration: weaponProperties.reloadTime,
      weapon: weaponProperties.name,
      weaponName: weaponProperties.prettyName,
    });
    time += weaponProperties.reloadTime;
  }

  return events;
}

/**
 * Generate events for fire-and-forget missiles
 */
function generateFireAndForgetMissileEvents(
  hp: number,
  weaponProperties: {
    name: string;
    prettyName: string;
    aimTime: number;
    reloadTime: number;
    timeBetweenShots: number;
    salvoLength: number;
    damagePerShot: number;
    accuracy: number;
    flightTimeOfOneMissile: number;
    simultaneousProjectiles: number;
  }
): WeaponEvent[] {
  const events: WeaponEvent[] = [];
  let time = 0;

  events.push({
    time,
    type: 'aim',
    weapon: weaponProperties.name,
    duration: weaponProperties.aimTime,
    weaponName: weaponProperties.prettyName,
  });

  time += weaponProperties.aimTime;

  while (hp > 0) {
    for (let shotNumber = 0; shotNumber < weaponProperties.salvoLength; shotNumber++) {
      const timeWhenNextShotReady = time + weaponProperties.timeBetweenShots;
      const timeWhenMissileHitsTarget = time + weaponProperties.flightTimeOfOneMissile;

      for (let j = 0; j < weaponProperties.simultaneousProjectiles; j++) {
        events.push({
          time,
          type: 'missile-launch',
          weapon: weaponProperties.name,
          weaponName: weaponProperties.prettyName,
          duration: weaponProperties.flightTimeOfOneMissile,
        });

        const didMissileHit = didEventHit(weaponProperties.accuracy);
        if (didMissileHit) hp -= weaponProperties.damagePerShot;

        events.push({
          time: timeWhenMissileHitsTarget,
          type: 'missile-hit',
          damage: weaponProperties.damagePerShot,
          weapon: weaponProperties.name,
          missileTravelTimeToTarget: weaponProperties.flightTimeOfOneMissile,
          accuracy: weaponProperties.accuracy,
          weaponName: weaponProperties.prettyName,
          hit: didMissileHit,
          remainingHp: hp,
        });

        if (j > 1) {
          shotNumber++;
        }
      }

      if (shotNumber < weaponProperties.salvoLength - 1) {
        time = timeWhenNextShotReady;
      }
    }

    if (hp <= 0) {
      break;
    }

    time += weaponProperties.reloadTime;

    events.push({
      time,
      type: 'reload',
      duration: weaponProperties.reloadTime,
      weapon: weaponProperties.name,
      weaponName: weaponProperties.prettyName,
    });
  }

  return events;
}

/**
 * Generate events for guided missiles
 */
function generateGuidedMissileEvents(
  hp: number,
  weaponProperties: {
    name: string;
    prettyName: string;
    aimTime: number;
    reloadTime: number;
    timeBetweenShots: number;
    salvoLength: number;
    damagePerShot: number;
    accuracy: number;
    flightTimeOfOneMissile: number;
    simultaneousProjectiles: number;
  }
): WeaponEvent[] {
  const events: WeaponEvent[] = [];
  let time = 0;

  events.push({
    time,
    type: 'aim',
    duration: weaponProperties.aimTime,
    weapon: weaponProperties.name,
    weaponName: weaponProperties.prettyName,
  });

  time += weaponProperties.aimTime;

  while (hp > 0) {
    for (let shotNumber = 0; shotNumber < weaponProperties.salvoLength; shotNumber++) {
      const timeWhenMissileHitsTarget = time + weaponProperties.flightTimeOfOneMissile;

      for (let j = 0; j < weaponProperties.simultaneousProjectiles; j++) {
        events.push({
          time,
          type: 'missile-launch',
          weapon: weaponProperties.name,
          weaponName: weaponProperties.prettyName,
          duration: weaponProperties.flightTimeOfOneMissile,
        });

        const didMissileHit = didEventHit(weaponProperties.accuracy);

        if (didMissileHit) hp -= weaponProperties.damagePerShot;

        events.push({
          time: timeWhenMissileHitsTarget,
          type: 'missile-hit',
          damage: weaponProperties.damagePerShot,
          weapon: weaponProperties.name,
          missileTravelTimeToTarget: weaponProperties.flightTimeOfOneMissile,
          accuracy: weaponProperties.accuracy,
          weaponName: weaponProperties.prettyName,
          hit: didMissileHit,
          remainingHp: hp,
        });

        if (j > 1) {
          shotNumber++;
        }
      }

      time = timeWhenMissileHitsTarget;

      if (shotNumber < weaponProperties.salvoLength - 1) {
        time += weaponProperties.timeBetweenShots;
      }

      if (hp <= 0) {
        break;
      }
    }

    if (hp <= 0) {
      break;
    }

    events.push({
      time,
      type: 'reload',
      duration: weaponProperties.reloadTime,
      weapon: weaponProperties.name,
      weaponName: weaponProperties.prettyName,
    });
    time += weaponProperties.reloadTime;
  }

  return events;
}

/**
 * Generate events for weapon kill
 */
function generateEventsForWeaponKill(
  baseHp: number,
  weaponProperties: {
    name: string;
    prettyName: string;
    aimTime: number;
    reloadTime: number;
    timeBetweenShots: number;
    salvoLength: number;
    damagePerShot: number;
    accuracy: number;
    simultaneousProjectiles: number;
    missileOptions?: {
      fireAndForget: boolean;
      missileTravelTimeToTarget: number;
    };
  }
): { events: WeaponEvent[]; dps: number } {
  let remainingHp = baseHp;
  let eventsForWeapon: WeaponEvent[] = [];

  if (weaponProperties.damagePerShot > 0.01) {
    if (weaponProperties.missileOptions) {
      const isFireAndForget = weaponProperties.missileOptions.fireAndForget;
      const flightTimeOfOneMissile = weaponProperties.missileOptions.missileTravelTimeToTarget;

      if (isFireAndForget) {
        eventsForWeapon = generateFireAndForgetMissileEvents(remainingHp, {
          name: weaponProperties.name,
          prettyName: weaponProperties.prettyName,
          aimTime: weaponProperties.aimTime,
          reloadTime: weaponProperties.reloadTime,
          timeBetweenShots: weaponProperties.timeBetweenShots,
          salvoLength: weaponProperties.salvoLength,
          damagePerShot: weaponProperties.damagePerShot,
          accuracy: weaponProperties.accuracy,
          flightTimeOfOneMissile,
          simultaneousProjectiles: weaponProperties.simultaneousProjectiles,
        });
      } else {
        eventsForWeapon = generateGuidedMissileEvents(remainingHp, {
          name: weaponProperties.name,
          prettyName: weaponProperties.prettyName,
          aimTime: weaponProperties.aimTime,
          reloadTime: weaponProperties.reloadTime,
          timeBetweenShots: weaponProperties.timeBetweenShots,
          salvoLength: weaponProperties.salvoLength,
          damagePerShot: weaponProperties.damagePerShot,
          accuracy: weaponProperties.accuracy,
          flightTimeOfOneMissile,
          simultaneousProjectiles: weaponProperties.simultaneousProjectiles,
        });
      }
    } else {
      eventsForWeapon = generateGunEvents(remainingHp, {
        name: weaponProperties.name,
        prettyName: weaponProperties.prettyName,
        aimTime: weaponProperties.aimTime,
        reloadTime: weaponProperties.reloadTime,
        timeBetweenShots: weaponProperties.timeBetweenShots,
        salvoLength: weaponProperties.salvoLength,
        damagePerShot: weaponProperties.damagePerShot,
        accuracy: weaponProperties.accuracy,
        simultaneousProjectiles: weaponProperties.simultaneousProjectiles,
      });
    }
  }

  const dps = calculateDPS(eventsForWeapon, remainingHp);

  return {
    events: eventsForWeapon,
    dps,
  };
}

/**
 * Stress (suppression) recovery per second for the target from rookie / helo rookie veterancy effects.
 */
function getTargetSuppressRegenPerSecond(
  targetVeterancy: Veterancy | undefined
): number | undefined {
  if (targetVeterancy === undefined) {
    return undefined;
  }
  const mod = VETERANCY_MODIFIERS_MAP[targetVeterancy];
  const tuple = mod?.suppressRegen;
  const perSec = tuple?.[1];
  if (perSec === undefined || perSec <= 0) {
    return undefined;
  }
  return perSec;
}

/**
 * Process events chronologically and track remaining HP.
 * When `suppressRegenPerSecond` is set (suppression simulation only), applies target stress recovery between events.
 */
function processEvents(
  events: WeaponEvent[],
  totalHp: number,
  suppressRegenPerSecond?: number
): WeaponEvent[] {
  const eventsByTime = [...events].sort((a, b) => a.time - b.time);

  if (eventsByTime.length === 0) {
    return [];
  }

  if (suppressRegenPerSecond != null && suppressRegenPerSecond > 0) {
    let remainingHp = totalHp;
    let lastTime = 0;
    let killIndex = eventsByTime.length - 1;

    for (let eventIndex = 0; eventIndex < eventsByTime.length; eventIndex++) {
      const event = eventsByTime[eventIndex];
      if (!event) {
        break;
      }
      const dt = event.time - lastTime;
      if (dt > 0) {
        remainingHp = Math.min(totalHp, remainingHp + suppressRegenPerSecond * dt);
      }
      lastTime = event.time;

      if (event.type === 'shot' || event.type === 'missile-hit') {
        if (event.hit) {
          remainingHp -= event.damage || 0;
        }
        event.remainingHp = remainingHp;
      }

      if (remainingHp <= 0) {
        killIndex = eventIndex;
        break;
      }
    }

    return eventsByTime.slice(0, killIndex + 1);
  }

  let remainingHp = totalHp;
  let eventIndex = 0;

  let killIndex = 0;
  while (remainingHp > 0) {
    const event = eventsByTime[eventIndex];
    if (!event) {
      break;
    }
    if (event.type === 'shot' || event.type === 'missile-hit') {
      if (event.hit) {
        remainingHp -= event.damage || 0;
      }
      event.remainingHp = remainingHp;
    }

    if (remainingHp <= 0) {
      killIndex = eventIndex;
      break;
    }

    eventIndex++;
  }

  return eventsByTime.slice(0, killIndex + 1);
}

/**
 * Calculate DPS from events
 */
function calculateDPS(_events: WeaponEvent[], _startingHealth: number): number {
  if (_events.length === 0) {
    return 0;
  }

  const events = [..._events];
  // throw away the aim event
  events.shift();

  if (events.length === 0) {
    return 0;
  }

  const startTime = events[0]!.time;
  const endTime = events[events.length - 1]!.time;
  const startingHealth = _startingHealth;

  // find last event with remainingHp present
  const lastEventWithHp = [...events].reverse().find((event): event is ShotEvent | MissileHitEvent =>
    (event.type === 'shot' || event.type === 'missile-hit') && event.remainingHp !== undefined
  );

  if (!lastEventWithHp) {
    return 0;
  }

  const endingHealth = lastEventWithHp.remainingHp!;

  const timeElapsed = endTime - startTime;
  const healthLost = startingHealth - endingHealth;
  const dps = healthLost / timeElapsed;

  return dps;
}

/**
 * Simulate kill using event-based approach
 */
function simulateKill(
  weapon: WeaponData,
  sourceUnit: Unit,
  targetUnit: Unit,
  damageTableProcessor: DamageTableProcessor,
  distance: number,
  side: Side,
  terrain: string,
  motion: Motion,
  sourceVeterancy: Veterancy | undefined,
  targetVeterancy: Veterancy | undefined,
  cohesion: Cohesion | undefined,
  resultType: EventResultType,
  sourceTraitOverrides?: Map<string, boolean | undefined>,
  targetTraitOverrides?: Map<string, boolean | undefined>
): { events: WeaponEvent[]; dps: number } {
  const events: WeaponEvent[] = [];
  let dpsMap: { [key: string]: number; totalDps: number } = { totalDps: 0 };

  const healthOfUnit = targetUnit.maxDamage || 0;

  // Extract damage stats for the best damage family
  const {
    damageFamily,
    damage,
    simultaneousProjectiles,
    missileSpeed,
    missileAcceleration,
    suppressionDamage: _suppressionDamage,
    damageMultiplier,
    suppressionMultiplier: _suppressionMultiplier,
  } = extractAmmunitionForTargetStats(
    weapon,
    targetUnit,
    damageTableProcessor,
    distance,
    side,
    terrain,
    targetVeterancy,
    sourceUnit,
    motion,
    sourceTraitOverrides,
    targetTraitOverrides
  );

  const isMoving = motion === Motion.MOVING;

  const baseAccuracy = getAccuracy(
    weapon,
    distance,
    isMoving,
    targetUnit.movementType
  );

  const baseAimTime = weapon.ammo.aimingTime || 0;
  const baseReloadTime = weapon.ammo.reloadTime || 0;
  const salvoLength = weapon.ammo.salvoLength || 0;
  let timeBetweenShots = weapon.ammo.timeBetweenSalvos || 0;

  const modifierResult = applyModifiersToWeaponStats(
    damageFamily,
    baseAccuracy,
    baseAimTime,
    baseReloadTime,
    isMoving,
    sourceVeterancy,
    targetVeterancy,
    cohesion,
    targetUnit,
    sourceUnit,
    distance,
    motion,
    sourceTraitOverrides,
    targetTraitOverrides
  );
  const { accuracy, aimTime, reloadTime, timeBetweenShots: traitTimeBetweenShots } = modifierResult;
  
  // Apply time between shots modifier if present
  if (traitTimeBetweenShots !== undefined) {
    timeBetweenShots *= traitTimeBetweenShots;
  }

  const accuracyAsDecimal = accuracy / 100;
  let damagePerShot = damage;
  let accuracyForEvents = accuracyAsDecimal;

  if (resultType === EventResultType.OPTIMAL) {
    accuracyForEvents = 1;
  } else if (resultType === EventResultType.AVERAGE) {
    damagePerShot = damage * accuracyAsDecimal;
    accuracyForEvents = 1;
  }

  // Calculate missile options
  let missileOptions: { fireAndForget: boolean; missileTravelTimeToTarget: number } | undefined;
  if (missileSpeed && missileAcceleration) {
    const missileTravelTimeToTarget = calculateDistanceTravelledByMissile(
      distance,
      missileSpeed,
      missileAcceleration
    );
    const isFireAndForget =
      (weapon.ammo.traits?.includes('F&F') || false) ||
      ((weapon as any)?.traits?.includes('F&F') || false);
    missileOptions = {
      fireAndForget: isFireAndForget,
      missileTravelTimeToTarget,
    };
  }

  const eventProperties = generateEventsForWeaponKill(healthOfUnit, {
    name: weapon.ammo.descriptorName || weapon.ammoDescriptors?.[0] || 'unknown',
    prettyName: weapon.ammo.name || 'Unknown',
    aimTime,
    reloadTime,
    timeBetweenShots,
    salvoLength,
    damagePerShot,
    accuracy: accuracyForEvents,
    missileOptions,
    simultaneousProjectiles,
  });

  events.push(...eventProperties.events);
  dpsMap[weapon.ammo.descriptorName || weapon.ammoDescriptors?.[0] || 'unknown'] = eventProperties.dps;
  dpsMap.totalDps += eventProperties.dps;

  const eventsByTime = processEvents(events, healthOfUnit);

  return { events: eventsByTime, dps: dpsMap.totalDps };
}

/**
 * Simulate suppression using event-based approach
 */
function simulateSuppression(
  weapon: WeaponData,
  sourceUnit: Unit,
  targetUnit: Unit,
  damageTableProcessor: DamageTableProcessor,
  distance: number,
  side: Side,
  terrain: string,
  motion: Motion,
  sourceVeterancy: Veterancy | undefined,
  targetVeterancy: Veterancy | undefined,
  cohesion: Cohesion | undefined,
  resultType: EventResultType,
  sourceTraitOverrides?: Map<string, boolean | undefined>,
  targetTraitOverrides?: Map<string, boolean | undefined>
): { events: WeaponEvent[]; dps: number } {
  const events: WeaponEvent[] = [];
  let dpsMap: { [key: string]: number; totalDps: number } = { totalDps: 0 };
  const healthOfUnit = MAX_SUPPRESSION;

  // Extract damage stats for the best damage family
  const {
    damageFamily,
    damage: _damage,
    simultaneousProjectiles,
    missileSpeed,
    missileAcceleration,
    suppressionDamage,
    damageMultiplier: _damageMultiplier,
    suppressionMultiplier: _suppressionMultiplier,
  } = extractAmmunitionForTargetStats(
    weapon,
    targetUnit,
    damageTableProcessor,
    distance,
    side,
    terrain,
    targetVeterancy,
    sourceUnit,
    motion,
    sourceTraitOverrides,
    targetTraitOverrides
  );

  const isMoving = motion === Motion.MOVING;

  const baseAccuracy = getAccuracy(
    weapon,
    distance,
    isMoving,
    targetUnit.movementType
  );

  const baseAimTime = weapon.ammo.aimingTime || 0;
  const baseReloadTime = weapon.ammo.reloadTime || 0;
  const salvoLength = weapon.ammo.salvoLength || 0;
  let timeBetweenShots = weapon.ammo.timeBetweenSalvos || 0;

  const modifierResult = applyModifiersToWeaponStats(
    damageFamily,
    baseAccuracy,
    baseAimTime,
    baseReloadTime,
    isMoving,
    sourceVeterancy,
    targetVeterancy,
    cohesion,
    targetUnit,
    sourceUnit,
    distance,
    motion,
    sourceTraitOverrides,
    targetTraitOverrides
  );
  const { accuracy, aimTime, reloadTime, timeBetweenShots: traitTimeBetweenShots } = modifierResult;
  
  // Apply time between shots modifier if present
  if (traitTimeBetweenShots !== undefined) {
    timeBetweenShots *= traitTimeBetweenShots;
  }

  const accuracyAsDecimal = accuracy / 100;
  let suppressPerShot = suppressionDamage;
  let accuracyForEvents = accuracyAsDecimal;

  if (resultType === EventResultType.OPTIMAL) {
    accuracyForEvents = 1;
  } else if (resultType === EventResultType.AVERAGE) {
    suppressPerShot = suppressionDamage * accuracyAsDecimal;
    accuracyForEvents = 1;
  }

  // Calculate missile options
  let missileOptions: { fireAndForget: boolean; missileTravelTimeToTarget: number } | undefined;
  if (missileSpeed && missileAcceleration) {
    const missileTravelTimeToTarget = calculateDistanceTravelledByMissile(
      distance,
      missileSpeed,
      missileAcceleration
    );
    const isFireAndForget =
      (weapon.ammo.traits?.includes('F&F') || false) ||
      ((weapon as any)?.traits?.includes('F&F') || false);
    missileOptions = {
      fireAndForget: isFireAndForget,
      missileTravelTimeToTarget,
    };
  }

  const eventProperties = generateEventsForWeaponKill(healthOfUnit, {
    name: weapon.ammo.descriptorName || weapon.ammoDescriptors?.[0] || 'unknown',
    prettyName: weapon.ammo.name || 'Unknown',
    aimTime,
    reloadTime,
    timeBetweenShots,
    salvoLength,
    damagePerShot: suppressPerShot,
    accuracy: accuracyForEvents,
    missileOptions,
    simultaneousProjectiles,
  });

  events.push(...eventProperties.events);
  dpsMap[weapon.ammo.descriptorName || weapon.ammoDescriptors?.[0] || 'unknown'] = eventProperties.dps;
  dpsMap.totalDps += eventProperties.dps;

  const eventsByTime = processEvents(events, healthOfUnit);

  return { events: eventsByTime, dps: dpsMap.totalDps };
}

/**
 * Calculate damage for weapons against a target unit using event-based simulation
 * Matches WarYes simulateKill approach - merges events from all weapons
 */
export async function calculateDamage(
  weapons: WeaponData[],
  sourceUnit: Unit,
  targetUnit: Unit,
  damageTable: DamageTable,
  distance: number,
  side: Side,
  terrain: string,
  motion: Motion,
  sourceVeterancy?: Veterancy,
  targetVeterancy?: Veterancy,
  cohesion?: Cohesion,
  sourceTraitOverrides?: Map<string, boolean | undefined>,
  targetTraitOverrides?: Map<string, boolean | undefined>
): Promise<DamageCalculationResult | null> {
  if (!weapons || weapons.length === 0 || !targetUnit || !damageTable) {
    return null;
  }

  const damageTableProcessor = new DamageTableProcessor(damageTable);
  const healthOfUnit = targetUnit.maxDamage || 0;

  // Track active traits
  const activeTraits = {
    source: [] as Array<{
      traitName: string;
      effects: string[];
      isActive: boolean;
      activationReason?: string;
      activationType: 'auto' | 'manual' | 'unknown';
    }>,
    target: [] as Array<{
      traitName: string;
      effects: string[];
      isActive: boolean;
      activationReason?: string;
      activationType: 'auto' | 'manual' | 'unknown';
    }>
  };

  // Check all source unit traits
  if (sourceUnit.specialities) {
    const traitModifiersMap = getTraitModifiersMap();
    const context: TraitActivationContext = {
      sourceUnit,
      targetUnit,
      distance,
      sourceMotion: motion,
      targetMotion: motion,
      sourceTraitOverrides,
      targetTraitOverrides: undefined
    };

    for (const traitName of sourceUnit.specialities) {
      if (traitName.startsWith('_')) {
        const activationResult = isTraitActive(traitName, context, true);
        const traitModifier = traitModifiersMap[traitName];
        
        if (traitModifier) {
          activeTraits.source.push({
            traitName,
            effects: getTraitEffectDescriptions(traitName, traitModifier),
            isActive: activationResult.isActive,
            activationReason: activationResult.activationReason,
            activationType: activationResult.activationType
          });
        } else if (activationResult.isActive || activationResult.activationType === 'manual') {
          // Trait is active but no modifier found (might be a tag-only trait)
          activeTraits.source.push({
            traitName,
            effects: [],
            isActive: activationResult.isActive,
            activationReason: activationResult.activationReason,
            activationType: activationResult.activationType
          });
        }
      }
    }
  }

  // Check all target unit traits
  if (targetUnit.specialities) {
    const traitModifiersMap = getTraitModifiersMap();
    const context: TraitActivationContext = {
      sourceUnit,
      targetUnit,
      distance,
      sourceMotion: motion,
      targetMotion: Motion.STATIC, // Assume target is always static
      sourceTraitOverrides: undefined,
      targetTraitOverrides
    };

    for (const traitName of targetUnit.specialities) {
      if (traitName.startsWith('_')) {
        const activationResult = isTraitActive(traitName, context, false);
        const traitModifier = traitModifiersMap[traitName];
        
        if (traitModifier) {
          activeTraits.target.push({
            traitName,
            effects: getTraitEffectDescriptions(traitName, traitModifier),
            isActive: activationResult.isActive,
            activationReason: activationResult.activationReason,
            activationType: activationResult.activationType
          });
        } else if (activationResult.isActive || activationResult.activationType === 'manual') {
          // Trait is active but no modifier found
          activeTraits.target.push({
            traitName,
            effects: [],
            isActive: activationResult.isActive,
            activationReason: activationResult.activationReason,
            activationType: activationResult.activationType
          });
        }
      }
    }
  }

  // Check if any weapon can target unit
  let canTarget = false;
  for (const weapon of weapons) {
    const maxRange = getMaxRangeOfWeaponTargettingUnit(weapon, targetUnit);
    const minRange = getMinRangeOfWeaponTargettingUnit(weapon, targetUnit);
    if (maxRange > 0 && distance >= minRange && distance <= maxRange) {
      canTarget = true;
      break;
    }
  }

  if (!canTarget) {
    return {
      damage: 0,
      damagePerSecond: 0,
      averageDamagePerSecond: 0,
      damageMultiplier: 0,
      suppressionDamage: 0,
      suppressionMultiplier: 0,
      timeToKill: 0,
      shotsToKill: 0,
      shotsToKillWithAccuracy: 0,
      shotsToMaxSuppression: 0,
      averageTimeToKill: 0,
      suppressionTimeToKill: 0,
      suppressionTimeToKillWithAccuracy: 0,
      shotsToMaxSuppressionWithAccuracy: 0,
      accuracy: 0,
      canTarget: false,
    };
  }

  // Simulate optimal kill - merge events from all weapons
  const optimalKillEvents: WeaponEvent[] = [];
  let optimalDpsMap: { [key: string]: number; totalDps: number } = { totalDps: 0 };

  for (const weapon of weapons) {
    const maxRange = getMaxRangeOfWeaponTargettingUnit(weapon, targetUnit);
    const minRange = getMinRangeOfWeaponTargettingUnit(weapon, targetUnit);
    if (maxRange === 0 || distance < minRange || distance > maxRange) {
      continue;
    }

    const result = simulateKill(
      weapon,
      sourceUnit,
      targetUnit,
      damageTableProcessor,
      distance,
      side,
      terrain,
      motion,
      sourceVeterancy,
      targetVeterancy,
      cohesion,
      EventResultType.OPTIMAL,
      sourceTraitOverrides,
      targetTraitOverrides
    );

    optimalKillEvents.push(...result.events);
    optimalDpsMap.totalDps += result.dps;
  }

  const optimalKillProcessed = processEvents(optimalKillEvents, healthOfUnit);

  // Simulate average kill - merge events from all weapons
  const averageKillEvents: WeaponEvent[] = [];
  let averageDpsMap: { [key: string]: number; totalDps: number } = { totalDps: 0 };

  for (const weapon of weapons) {
    const maxRange = getMaxRangeOfWeaponTargettingUnit(weapon, targetUnit);
    const minRange = getMinRangeOfWeaponTargettingUnit(weapon, targetUnit);
    if (maxRange === 0 || distance < minRange || distance > maxRange) {
      continue;
    }

    const result = simulateKill(
      weapon,
      sourceUnit,
      targetUnit,
      damageTableProcessor,
      distance,
      side,
      terrain,
      motion,
      sourceVeterancy,
      targetVeterancy,
      cohesion,
      EventResultType.AVERAGE,
      sourceTraitOverrides,
      targetTraitOverrides
    );

    averageKillEvents.push(...result.events);
    averageDpsMap.totalDps += result.dps;
  }

  const averageKillProcessed = processEvents(averageKillEvents, healthOfUnit);

  // Simulate optimal suppression - merge events from all weapons
  const optimalSuppressEvents: WeaponEvent[] = [];
  let optimalSuppressDpsMap: { [key: string]: number; totalDps: number } = { totalDps: 0 };

  for (const weapon of weapons) {
    const maxRange = getMaxRangeOfWeaponTargettingUnit(weapon, targetUnit);
    const minRange = getMinRangeOfWeaponTargettingUnit(weapon, targetUnit);
    if (maxRange === 0 || distance < minRange || distance > maxRange) {
      continue;
    }

    const result = simulateSuppression(
      weapon,
      sourceUnit,
      targetUnit,
      damageTableProcessor,
      distance,
      side,
      terrain,
      motion,
      sourceVeterancy,
      targetVeterancy,
      cohesion,
      EventResultType.OPTIMAL,
      sourceTraitOverrides,
      targetTraitOverrides
    );

    optimalSuppressEvents.push(...result.events);
    optimalSuppressDpsMap.totalDps += result.dps;
  }

  const targetSuppressRegen = getTargetSuppressRegenPerSecond(targetVeterancy);
  const optimalSuppressProcessed = processEvents(
    optimalSuppressEvents,
    MAX_SUPPRESSION,
    targetSuppressRegen
  );

  // Simulate average suppression - merge events from all weapons
  const averageSuppressEvents: WeaponEvent[] = [];
  let averageSuppressDpsMap: { [key: string]: number; totalDps: number } = { totalDps: 0 };

  for (const weapon of weapons) {
    const maxRange = getMaxRangeOfWeaponTargettingUnit(weapon, targetUnit);
    const minRange = getMinRangeOfWeaponTargettingUnit(weapon, targetUnit);
    if (maxRange === 0 || distance < minRange || distance > maxRange) {
      continue;
    }

    const result = simulateSuppression(
      weapon,
      sourceUnit,
      targetUnit,
      damageTableProcessor,
      distance,
      side,
      terrain,
      motion,
      sourceVeterancy,
      targetVeterancy,
      cohesion,
      EventResultType.AVERAGE,
      sourceTraitOverrides,
      targetTraitOverrides
    );

    averageSuppressEvents.push(...result.events);
    averageSuppressDpsMap.totalDps += result.dps;
  }

  const averageSuppressProcessed = processEvents(
    averageSuppressEvents,
    MAX_SUPPRESSION,
    targetSuppressRegen
  );

  // Extract results from processed events
  // Count shots to kill (all shot and missile-hit events)
  const shotsToKill = optimalKillProcessed.filter(
    (e) => e.type === 'shot' || e.type === 'missile-hit'
  ).length;
  const shotsToKillWithAccuracy = averageKillProcessed.filter(
    (e) => e.type === 'shot' || e.type === 'missile-hit'
  ).length;

  // Count shots to suppress
  const shotsToMaxSuppression = optimalSuppressProcessed.filter(
    (e) => e.type === 'shot' || e.type === 'missile-hit'
  ).length;
  const shotsToMaxSuppressionWithAccuracy = averageSuppressProcessed.filter(
    (e) => e.type === 'shot' || e.type === 'missile-hit'
  ).length;

  // Get time to kill from final event
  const finalOptimalKillEvent = optimalKillProcessed[optimalKillProcessed.length - 1];
  const finalAverageKillEvent = averageKillProcessed[averageKillProcessed.length - 1];
  const finalOptimalSuppressEvent = optimalSuppressProcessed[optimalSuppressProcessed.length - 1];
  const finalAverageSuppressEvent = averageSuppressProcessed[averageSuppressProcessed.length - 1];

  const timeToKill = finalOptimalKillEvent?.time || 0;
  const averageTimeToKill = finalAverageKillEvent?.time || 0;
  const suppressionTimeToKill = finalOptimalSuppressEvent?.time || 0;
  const suppressionTimeToKillWithAccuracy = finalAverageSuppressEvent?.time || 0;

  // Calculate DPS from processed events (matches WarYes implementation)
  const damagePerSecond = optimalDpsMap.totalDps;
  const averageDamagePerSecond = averageDpsMap.totalDps;

  // Get damage stats from first weapon for display (use best damage family)
  const firstWeapon = weapons[0];
  if (!firstWeapon) {
    return null;
  }

  const {
    damageFamily,
    damage,
    missileSpeed,
    missileAcceleration,
    suppressionDamage,
    damageMultiplier,
    suppressionMultiplier,
  } = extractAmmunitionForTargetStats(
    firstWeapon,
    targetUnit,
    damageTableProcessor,
    distance,
    side,
    terrain,
    targetVeterancy,
    sourceUnit,
    motion,
    sourceTraitOverrides,
    targetTraitOverrides
  );

  const isMoving = motion === Motion.MOVING;
  const baseAccuracy = getAccuracy(
    firstWeapon,
    distance,
    isMoving,
    targetUnit.movementType
  );

  const { accuracy } = applyModifiersToWeaponStats(
    damageFamily,
    baseAccuracy,
    firstWeapon.ammo.aimingTime || 0,
    firstWeapon.ammo.reloadTime || 0,
    isMoving,
    sourceVeterancy,
    targetVeterancy,
    cohesion,
    targetUnit,
    sourceUnit,
    distance,
    motion,
    sourceTraitOverrides,
    targetTraitOverrides
  );

  // Get flight time of one missile if applicable
  let flightTimeOfOneMissile: number | undefined;
  if (missileSpeed && missileAcceleration) {
    flightTimeOfOneMissile = calculateDistanceTravelledByMissile(
      distance,
      missileSpeed,
      missileAcceleration
    );
  }

  return {
    damage,
    damagePerSecond,
    averageDamagePerSecond,
    damageMultiplier,
    suppressionDamage,
    suppressionMultiplier,
    flightTimeOfOneMissile,
    timeToKill,
    shotsToKill,
    shotsToKillWithAccuracy,
    shotsToMaxSuppression,
    averageTimeToKill,
    suppressionTimeToKill,
    suppressionTimeToKillWithAccuracy,
    shotsToMaxSuppressionWithAccuracy,
    accuracy,
    canTarget: true,
    activeTraits,
  };
}

/**
 * Extract ammunition stats for target
 * Matches WarYes implementation exactly
 */
function extractAmmunitionForTargetStats(
  sourceWeapon: WeaponData,
  targetUnit: Unit,
  damageTableProcessor: DamageTableProcessor,
  distance: number,
  side: Side,
  terrain: string,
  targetVeterancy?: Veterancy,
  sourceUnit?: Unit,
  sourceMotion?: Motion,
  sourceTraitOverrides?: Map<string, boolean | undefined>,
  targetTraitOverrides?: Map<string, boolean | undefined>
) {
  let highestDamage = 0;
  let highestDamageMultiplier = 0;
  let highestDamageSuppressionMultiplier = 0;
  let highestDamageSuppressionDamage = 0;
  let highestDamageSimultaneousProjectiles = 0;
  let highestDamageFamily = '';

  const missileSpeed = sourceWeapon.missile?.maxMissileSpeed;
  const missileAcceleration = sourceWeapon.missile?.maxMissileAcceleration;

  const iterLength = sourceWeapon.damageFamilies?.length || 0;
  const isEra = targetUnit.era || false;

  for (let i = 0; i < iterLength; i++) {
    const damageFamilyWithIndex = sourceWeapon.damageFamilies[i] as string;
    const isTandemCharge = sourceWeapon.tandemCharges?.[i] as boolean;
    const suppress =
      (sourceWeapon.suppressDamages?.[i] || 0) * sourceWeapon.numberOfWeapons || 0;
    const numberOfSimultaenousProjectiles =
      sourceWeapon.numberOfSimultaneousProjectiles?.[i] || 0;

    // Get damage and suppression multipliers
    // Note: DamageTableProcessor.getDamageMultiplier already handles damage drop-off internally
    const damageMultiplier = damageTableProcessor.getDamageMultiplier(
      { sourceWeapon, damageFamilyIndex: i },
      targetUnit,
      distance,
      side,
      terrain
    );

    const suppressionMultiplier = damageTableProcessor.getSuppressionMultiplier(
      { sourceWeapon, damageFamilyIndex: i },
      targetUnit,
      side
    );

    const family = extractDamageFamilyProperties(damageFamilyWithIndex);

    // Calculate base HE damage
    let he = sourceWeapon.totalHeDamage || 0;

    if (family.family === 'ap' || family.family === 'ap_missile') {
      he = 1;
    }

    // Calculate damage per hit
    let damage = he * damageMultiplier;

    // Calculate suppression per hit
    let suppressionVeterancyMultiplier = 1;

    if (targetVeterancy) {
      suppressionVeterancyMultiplier =
        VETERANCY_MODIFIERS_MAP[
          targetVeterancy as keyof typeof VETERANCY_MODIFIERS_MAP
        ]?.suppressionReceived || 1;
    }

    // Base suppression calculation
    let suppressionDamage =
      suppress * suppressionMultiplier * suppressionVeterancyMultiplier +
      EXTRA_SUPPRESSION_DAMAGE_PER_DAMAGE * Math.floor(damage);

    // Apply target trait suppression modifiers
    if (targetUnit && sourceMotion !== undefined) {
      const traitModifiersMap = getTraitModifiersMap();
      for (const traitName of targetUnit.specialities || []) {
        if (traitName.startsWith('_')) {
          const context: TraitActivationContext = {
            sourceUnit: sourceUnit || targetUnit,
            targetUnit,
            distance,
            sourceMotion,
            targetMotion: sourceMotion,
            sourceTraitOverrides: undefined,
            targetTraitOverrides
          };
          const activationResult = isTraitActive(traitName, context, false);
          if (activationResult.isActive) {
            const traitModifier = traitModifiersMap[traitName];
            if (traitModifier?.suppressionReceived) {
              suppressionDamage *= traitModifier.suppressionReceived;
            }
          }
        }
      }
    }

    // Apply ERA + tandem charge bonus
    if (isEra && isTandemCharge) {
      damage += 1;
    }

    // Apply trait weapon damage modifiers
    if (sourceUnit && sourceMotion !== undefined) {
      const traitModifiersMap = getTraitModifiersMap();
      for (const traitName of sourceUnit.specialities || []) {
        if (traitName.startsWith('_')) {
          const context: TraitActivationContext = {
            sourceUnit,
            targetUnit,
            distance,
            sourceMotion,
            targetMotion: sourceMotion, // Using same motion for now
            sourceTraitOverrides,
            targetTraitOverrides: undefined
          };
          const activationResult = isTraitActive(traitName, context, true);
          if (activationResult.isActive) {
            const traitModifier = traitModifiersMap[traitName];
            if (traitModifier?.weaponDamage) {
              damage *= traitModifier.weaponDamage;
            }
          }
        }
      }
    }

    // Track highest damage family
    if (damage > highestDamage) {
      highestDamage = damage;
      highestDamageMultiplier = damageMultiplier;
      highestDamageSuppressionMultiplier = suppressionMultiplier;
      highestDamageSuppressionDamage = suppressionDamage;
      highestDamageSimultaneousProjectiles = numberOfSimultaenousProjectiles;
      highestDamageFamily = family.family || '';
    }
  }

  return {
    damageFamily: highestDamageFamily || '',
    damage: highestDamage,
    simultaneousProjectiles: highestDamageSimultaneousProjectiles,
    missileSpeed,
    missileAcceleration,
    suppressionDamage: highestDamageSuppressionDamage,
    damageMultiplier: highestDamageMultiplier,
    suppressionMultiplier: highestDamageSuppressionMultiplier,
  };
}

/**
 * Apply modifiers to weapon stats
 */
function applyModifiersToWeaponStats(
  damageFamily: string,
  baseAccuracy: number,
  baseAimTime: number,
  baseReloadTime: number,
  isMoving: boolean,
  sourceVeterancy?: Veterancy,
  targetVeterancy?: Veterancy,
  cohesion?: Cohesion,
  targetUnit?: Unit,
  sourceUnit?: Unit,
  distance?: number,
  sourceMotion?: Motion,
  sourceTraitOverrides?: Map<string, boolean | undefined>,
  targetTraitOverrides?: Map<string, boolean | undefined>
): { accuracy: number; aimTime: number; reloadTime: number; timeBetweenShots?: number } {
  let ecmToApply = 0;

  if (damageFamily === 'missile_he') {
    ecmToApply = targetUnit?.ecm || 0;
  }

  let accuracy = baseAccuracy;
  let aimTime = baseAimTime;
  let reloadTime = baseReloadTime;
  let timeBetweenShots: number | undefined;

  // apply dodge first if applicable
  if (targetVeterancy) {
    const targetVeterancyModifier =
      VETERANCY_MODIFIERS_MAP[
        targetVeterancy as keyof typeof VETERANCY_MODIFIERS_MAP
      ];

    accuracy =
      accuracy -
      (((targetVeterancyModifier?.dodgeBonus?.[1] || 0) * 100) || 0);
  }

  if (sourceVeterancy) {
    const sourceVeterancyModifier =
      VETERANCY_MODIFIERS_MAP[
        sourceVeterancy as keyof typeof VETERANCY_MODIFIERS_MAP
      ];

    let accuracyModifier = sourceVeterancyModifier.staticAccuracy || ['+', 0];

    if (isMoving) {
      accuracyModifier = sourceVeterancyModifier.motionAccuracy || ['+', 0];
    }

    if (accuracyModifier[0] === '+') {
      accuracy = accuracy + accuracyModifier[1] * 100;
    } else if (accuracyModifier[0] === '-') {
      accuracy = accuracy - accuracyModifier[1] * 100;
    }

    aimTime = aimTime * (sourceVeterancyModifier.aimTime || 1);
    reloadTime = reloadTime * (sourceVeterancyModifier.reloadTime || 1);
  }

  if (cohesion) {
    const cohesionModifier =
      COHESION_MODIFIERS_MAP[cohesion as keyof typeof COHESION_MODIFIERS_MAP];
    accuracy = accuracy * cohesionModifier.accuracy;
    if ((cohesionModifier?.aimTime || 0) > 0) {
      aimTime = aimTime * (cohesionModifier?.aimTime || 0);
    }

    if ((cohesionModifier?.reloadTime || 0) > 0) {
      reloadTime = reloadTime * (cohesionModifier?.reloadTime || 0);
    }
  }

  // Apply source unit trait modifiers
  if (sourceUnit && distance !== undefined && sourceMotion !== undefined) {
    const traitModifiersMap = getTraitModifiersMap();
    const context: TraitActivationContext = {
      sourceUnit,
      targetUnit: targetUnit || sourceUnit,
      distance,
      sourceMotion,
      targetMotion: sourceMotion,
      sourceTraitOverrides,
      targetTraitOverrides: undefined
    };

    for (const traitName of sourceUnit.specialities || []) {
      if (traitName.startsWith('_')) {
        const activationResult = isTraitActive(traitName, context, true);
        if (activationResult.isActive) {
          const traitModifier = traitModifiersMap[traitName];
          if (!traitModifier) continue;

          // Apply accuracy modifiers
          if (traitModifier.staticAccuracy && !isMoving) {
            const [sign, value] = traitModifier.staticAccuracy;
            if (sign === '+') {
              accuracy = accuracy + value * 100;
            } else {
              accuracy = accuracy - value * 100;
            }
          }
          if (traitModifier.motionAccuracy && isMoving) {
            const [sign, value] = traitModifier.motionAccuracy;
            if (sign === '+') {
              accuracy = accuracy + value * 100;
            } else {
              accuracy = accuracy - value * 100;
            }
          }

          // Apply aim time modifier
          if (traitModifier.aimTime) {
            aimTime *= traitModifier.aimTime;
          }

          // Apply reload time modifier
          if (traitModifier.reloadTime) {
            reloadTime *= traitModifier.reloadTime;
          }

          // Store time between shots modifier
          if (traitModifier.timeBetweenShots) {
            timeBetweenShots = traitModifier.timeBetweenShots;
          }
        }
      }
    }
  }

  // Apply target unit trait modifiers (electronic warfare, EO dazzler)
  if (targetUnit && sourceMotion !== undefined) {
    const traitModifiersMap = getTraitModifiersMap();
    const context: TraitActivationContext = {
      sourceUnit: sourceUnit || targetUnit,
      targetUnit,
      distance: distance || 0,
      sourceMotion,
      targetMotion: Motion.STATIC, // Assume target is always static
      sourceTraitOverrides: undefined,
      targetTraitOverrides
    };

    for (const traitName of targetUnit.specialities || []) {
      if (traitName.startsWith('_')) {
        const activationResult = isTraitActive(traitName, context, false);
        if (activationResult.isActive) {
          const traitModifier = traitModifiersMap[traitName];
          if (!traitModifier) continue;

          // Electronic warfare reduces accuracy (percentage modifier)
          if (traitName === '_electronic_warfare') {
            if (traitModifier.staticAccuracy && !isMoving) {
              const [sign, value] = traitModifier.staticAccuracy;
              // Percentage modifier: -20% means multiply by 0.8
              if (sign === '-') {
                accuracy = accuracy * (1 - value);
              } else {
                accuracy = accuracy * (1 + value);
              }
            }
            if (traitModifier.motionAccuracy && isMoving) {
              const [sign, value] = traitModifier.motionAccuracy;
              // Percentage modifier: -20% means multiply by 0.8
              if (sign === '-') {
                accuracy = accuracy * (1 - value);
              } else {
                accuracy = accuracy * (1 + value);
              }
            }
          }


          // EO dazzler adds ECM bonus for missiles
          if (damageFamily === 'ap_missile' && traitName === '_eo_dazzler') {
            console.log(traitModifier.hitRollECM);
            const hitRollECM = traitModifier.hitRollECM;
            if (hitRollECM !== undefined) {
              ecmToApply += hitRollECM; // Negative value reduces ECM effectiveness
            }
          }
        }
      }
    }
  }


  // Apply ECM as a multiplicative modifier (matches in-game formula)
  accuracy = accuracy * (1 + ecmToApply);
  // cap accuracy at 100% after all modifiers
  accuracy = Math.min(accuracy, 100);
  return { accuracy, aimTime, reloadTime, timeBetweenShots };
}

/**
 * Get accuracy for weapon at distance
 */
function getAccuracy(
  weapon: WeaponData,
  distance: number,
  moving: boolean,
  movementType: MovementType
) {
  const accuracyScaling = getAccuracyScaling(weapon, moving, movementType);
  if (!accuracyScaling) {
    return getBaseAccuracy(weapon, moving);
  }

  // Find the two ranges that the distance is between
  let upperBoundAccuracy;
  let lowerBoundAccuracy;

  const minRange = getMinRangeForMovementType(weapon, movementType);

  // if the distance is less than the minimum range, use the minimum range accuracy
  if (accuracyScaling.length > 0 && accuracyScaling[0] && distance < accuracyScaling[0].distance) {
    upperBoundAccuracy = accuracyScaling[0];
    lowerBoundAccuracy = {
      accuracy: accuracyScaling[0].accuracy,
      distance: minRange,
    };
  } else {
    for (let i = 0; i < accuracyScaling.length; i++) {
      const scalingItem = accuracyScaling[i];
      if (!scalingItem) continue;
      const nextScalingItem = accuracyScaling[i + 1];

      if (nextScalingItem === undefined) {
        upperBoundAccuracy = scalingItem;
        lowerBoundAccuracy = scalingItem;
        break;
      }

      if (
        distance >= scalingItem.distance &&
        distance <= nextScalingItem.distance
      ) {
        upperBoundAccuracy = scalingItem;
        lowerBoundAccuracy = nextScalingItem;
        break;
      }
    }
  }

  if (!upperBoundAccuracy || !lowerBoundAccuracy) {
    return getBaseAccuracy(weapon, moving);
  }

  // get the accuracy at the interpolation value
  const interpolationValue =
    (distance - lowerBoundAccuracy.distance) /
    (upperBoundAccuracy.distance - lowerBoundAccuracy.distance);

  const upperBoundAccuracyValue = upperBoundAccuracy.accuracy;
  const lowerBoundAccuracyValue = lowerBoundAccuracy.accuracy;

  // Check if distance is less than minimum range after interpolation
  // (This check happens in DamageCalculator.ts but not in damage-calculator.ts)
  // We'll include it for consistency
  if (distance < minRange) {
    return 0;
  }

  const accuracy =
    lowerBoundAccuracyValue +
    (upperBoundAccuracyValue - lowerBoundAccuracyValue) * interpolationValue;

  return accuracy;
}

/**
 * Get accuracy scaling for movement type
 */
function getAccuracyScaling(
  weapon: WeaponData,
  moving: boolean,
  movementType: MovementType
) {
  const scaling = moving
    ? weapon.ammo.movingAccuracyOverDistance
    : weapon.ammo.staticAccuracyOverDistance;

  if (!scaling) {
    return undefined;
  }

  // AccuracyDataPointsForType has ground, plane, helicopter properties
  if (movementType === MovementType.LAND) {
    return scaling.ground;
  } else if (movementType === MovementType.PLANE) {
    return scaling.plane;
  } else if (movementType === MovementType.HELICOPTER) {
    return scaling.helicopter;
  }

  return undefined;
}

/**
 * Get base accuracy
 */
function getBaseAccuracy(weapon: WeaponData, moving: boolean) {
  if (moving) {
    return weapon.ammo.movingAccuracy || 0;
  }
  return weapon.ammo.staticAccuracy || 0;
}

/**
 * Get min range for movement type
 */
function getMinRangeForMovementType(
  weapon: WeaponData,
  movementType: MovementType
) {
  if (movementType === MovementType.LAND) {
    return weapon.ammo.groundMinRange || 0;
  } else if (movementType === MovementType.PLANE) {
    return weapon.ammo.planeMinRange || 0;
  } else if (movementType === MovementType.HELICOPTER) {
    return weapon.ammo.heliMinRange || 0;
  }
  return 0;
}

/**
 * Get max range for movement type
 */
function getMaxRangeForMovementType(
  weapon: WeaponData,
  movementType: MovementType
) {
  if (movementType === MovementType.LAND) {
    return weapon.ammo.groundMaxRange || 0;
  } else if (movementType === MovementType.PLANE) {
    return weapon.ammo.planeMaxRange || 0;
  } else if (movementType === MovementType.HELICOPTER) {
    return weapon.ammo.heliMaxRange || 0;
  }
  return 0;
}

/**
 * Calculate time to kill
 */
function calculateTimeToKill(
  shotsToKill: number,
  aimingTime: number,
  reloadTime: number,
  timeBetweenShots: number,
  salvoLength: number,
  numberOfSimultaneousProjectiles: number,
  distance: number,
  missileSpeed?: number,
  missileAcceleration?: number,
  weapon?: WeaponData
) {
  // number of shots divided by number of shots per salvo rounded up
  const numberOfReloads = Math.ceil(shotsToKill / salvoLength) - 1;
  const totalTimeReloading = numberOfReloads * reloadTime;

  let missileTravelTime = 0;
  let flightTimeOfOneMissile: number | undefined;

  if (missileSpeed && missileAcceleration) {
    // WarYes uses weapon.traits, but defcon-fulda has traits in weapon.ammo.traits
    // Check both locations to handle data structure differences
    const isFireAndForget = 
      (weapon?.ammo.traits?.includes('F&F') || false) ||
      ((weapon as any)?.traits?.includes('F&F') || false);

    const missileTimeToHitTarget = calculateDistanceTravelledByMissile(
      distance,
      missileSpeed,
      missileAcceleration
    );

    flightTimeOfOneMissile = missileTimeToHitTarget;

    if (isFireAndForget) {
      missileTravelTime = missileTimeToHitTarget;
    } else {
      missileTravelTime = missileTimeToHitTarget * shotsToKill;
    }
  }

  let instancesOfTimeBetweenShots = shotsToKill - 1 - numberOfReloads;

  if (
    numberOfSimultaneousProjectiles > 1 &&
    numberOfSimultaneousProjectiles <= salvoLength
  ) {
    const burstsToKill = Math.ceil(
      shotsToKill / numberOfSimultaneousProjectiles
    );
    const numberOfTimeBetweenShots = burstsToKill - 1;
    instancesOfTimeBetweenShots = numberOfTimeBetweenShots - numberOfReloads;

    if (flightTimeOfOneMissile && flightTimeOfOneMissile > 0) {
      missileTravelTime = flightTimeOfOneMissile * burstsToKill;
    }
  }

  const totalTimeBetweenShots = instancesOfTimeBetweenShots * timeBetweenShots;
  const timeToKill =
    aimingTime +
    totalTimeReloading +
    totalTimeBetweenShots +
    missileTravelTime;

  return { timeToKill, flightTimeOfOneMissile };
}

/**
 * Calculate distance travelled by missile
 */
function calculateDistanceTravelledByMissile(
  distance: number,
  missileSpeed: number,
  missileAcceleration: number
): number {
  let time = 0;

  const timeTilMaxVelocity = missileSpeed / missileAcceleration;
  const distanceTravelledTilMaxVelocity =
    0.5 * missileAcceleration * timeTilMaxVelocity * timeTilMaxVelocity;

  if (distanceTravelledTilMaxVelocity > distance) {
    // missile never reaches max velocity
    time = Math.sqrt((2 * distance) / missileAcceleration);
  } else {
    // missile reaches max velocity
    const distanceTravelledAfterMaxVelocity =
      distance - distanceTravelledTilMaxVelocity;
    const timeAfterMaxVelocity =
      distanceTravelledAfterMaxVelocity / missileSpeed;
    time = timeTilMaxVelocity + timeAfterMaxVelocity;
  }

  return time;
}

/**
 * Get max range of weapon targeting unit
 */
function getMaxRangeOfWeaponTargettingUnit(
  weapon: WeaponData,
  unit: Unit
): number {
  const movementType = unit.movementType;

  if (movementType === MovementType.LAND) {
    return weapon.ammo.groundMaxRange || 0;
  } else if (movementType === MovementType.PLANE) {
    return weapon.ammo.planeMaxRange || 0;
  } else if (movementType === MovementType.HELICOPTER) {
    return weapon.ammo.heliMaxRange || 0;
  }

  return 0;
}

/**
 * Get min range of weapon targeting unit
 */
function getMinRangeOfWeaponTargettingUnit(
  weapon: WeaponData,
  unit: Unit
): number {
  const movementType = unit.movementType;

  if (movementType === MovementType.LAND) {
    return weapon.ammo.groundMinRange || 0;
  } else if (movementType === MovementType.PLANE) {
    return weapon.ammo.planeMinRange || 0;
  } else if (movementType === MovementType.HELICOPTER) {
    return weapon.ammo.heliMinRange || 0;
  }

  return 0;
}
